<script lang="ts" setup>
import { financialConverter } from '@/converters/financials/financials-converter';
import { models } from 'cesg-shared';
import { computed, onMounted, type PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

type ProjectPickerMode = 'disabled' | 'readonly' | 'select';

const supplierInvoiceProject = ref<string | null>(null);

const { t } = useI18n();
const emit = defineEmits(['update']);

const props = defineProps({
  supplierInvoice: {
    type: Object as PropType<models.SupplierInvoiceResponse>,
    required: true,
    default: () => ({}) as models.SupplierInvoiceResponse,
  },
  disable: {
    type: Boolean,
    required: false,
    default: false,
  },
  projectPickerMode: {
    type: String as PropType<ProjectPickerMode>,
    required: false,
    default: undefined,
  },
});

const supplierInvoiceReadable = computed(() => {
  return financialConverter.toReadable(props.supplierInvoice);
});

const vatTypeTagColor = () => {
  return {
    normal: props.supplierInvoice.vatType === models.VatType.Normal,
    reverse: props.supplierInvoice.vatType === models.VatType.Reverse,
    'eu-internal': props.supplierInvoice.vatType === models.VatType.EuInternal,
  };
};

const warningVAT = () => {
  return {
    warning:
      props.supplierInvoice?.vatType === models.VatType.Reverse &&
      (props.supplierInvoice?.vat ?? 0) > 0,
  };
};

onMounted(() => {
  supplierInvoiceProject.value = props.supplierInvoice.project;
});

watch(
  () => supplierInvoiceProject.value,
  (newValue) => {
    emit('update', newValue);
  },
);
</script>

<template>
  <div class="columns is-multiline">
    <div class="column is-one-third">
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.invoiceNumber') }}</div>
        <div class="value">{{ supplierInvoiceReadable.invoiceNumber }}</div>
      </div>
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.organisationNumber') }}</div>
        <div class="value">
          {{ supplierInvoiceReadable.organisationNumber }}
        </div>
      </div>
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.zipCode') }}</div>
        <div class="value">{{ supplierInvoiceReadable.zipCode }}</div>
      </div>
    </div>

    <div class="column is-one-third">
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.supplierName') }}</div>
        <div class="value">{{ supplierInvoiceReadable.supplierName }}</div>
      </div>
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.address') }}</div>
        <div class="value">{{ supplierInvoiceReadable.address }}</div>
      </div>
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.city') }}</div>
        <div class="value">{{ supplierInvoiceReadable.city }}</div>
      </div>
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.invoiceDate') }}</div>
        <div class="value">{{ supplierInvoiceReadable.invoiceDate }}</div>
      </div>
    </div>

    <div class="column is-one-third">
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.dueDate') }}</div>
        <div class="value">{{ supplierInvoiceReadable.dueDate }}</div>
      </div>
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.termsOfPayment') }}</div>
        <div class="value">
          {{
            t('invoices.details.termsOfPaymentDays', {
              count: Number(supplierInvoiceReadable.termsOfPayment),
            })
          }}
        </div>
      </div>
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.vatType') }}</div>
        <div class="value">
          <span class="tag" :class="vatTypeTagColor()" data-test="vat-type">
            {{
              t(`invoices.details.vatTypes.${supplierInvoiceReadable.vatType}`)
            }}
          </span>
        </div>
      </div>
      <div class="key-value-pair">
        <div class="key">{{ t('invoices.details.vat') }}</div>
        <div class="value" :class="warningVAT()">
          {{ supplierInvoiceReadable.vat }} {{ t('SEK') }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.key-value-pair {
  display: flex;
  margin-bottom: 0.1rem;
  align-items: center;

  .key {
    flex: 0 0 40%;
    font-weight: bold;
  }

  .value {
    flex: 1;
    text-align: left;
    min-width: 0;
    max-width: 100%;
    display: flex;
    align-items: center;

    .tag {
      width: max-content;

      &.normal {
        background-color: var(--table-blue);
      }

      &.reverse {
        background-color: var(--table-green);
      }

      &.eu-internal {
        background-color: var(--table-orange);
      }
    }
    &.warning {
      color: var(--text-danger);
      font-weight: 500;
    }
  }
}
</style>
